import type { InfiniteData } from "@tanstack/react-query";

import {
  type ListNotificationsResponseDto,
  type NotificationDto,
  NotificationStatus,
} from "@/features/notifications/model/notifications.types";

/**
 * Pure paging and cache rules for the notification inbox, kept out of the hooks so they can be mutation-tested.
 * The bell panel caches pages (`InfiniteData`); the notification center caches one page at a time.
 */

export type NotificationListCache = ListNotificationsResponseDto | InfiniteData<ListNotificationsResponseDto, number>;

/** The next page to load while scrolling, or `undefined` once every row in the inbox window is loaded. */
export function getNextNotificationsPageParam(lastPage: ListNotificationsResponseDto): number | undefined {
  return lastPage.page * lastPage.limit < lastPage.total ? lastPage.page + 1 : undefined;
}

/**
 * Flattens loaded pages into display order, dropping archived rows. A poll can shift rows between pages when new
 * notifications arrive, so a row repeated on a later page is kept only once (first occurrence wins).
 */
export function flattenNotificationPages(pages: readonly ListNotificationsResponseDto[] | undefined): NotificationDto[] {
  const byId = new Map<string, NotificationDto>();
  for (const page of pages ?? []) {
    for (const notification of page.items) {
      if (notification.status === NotificationStatus.Archived || byId.has(notification.id)) continue;
      byId.set(notification.id, notification);
    }
  }
  return Array.from(byId.values());
}

export function isNotificationsListResponse(value: unknown): value is ListNotificationsResponseDto {
  if (!value || typeof value !== "object") return false;
  return Array.isArray((value as ListNotificationsResponseDto).items);
}

export function isNotificationsInfiniteListResponse(
  value: unknown,
): value is InfiniteData<ListNotificationsResponseDto, number> {
  if (!value || typeof value !== "object") return false;
  const pages = (value as InfiniteData<ListNotificationsResponseDto, number>).pages;
  return Array.isArray(pages) && pages.every(isNotificationsListResponse);
}

/** Applies a page transform to both cache shapes; anything unrecognised is returned untouched. */
export function mapNotificationListCache(
  current: NotificationListCache | undefined,
  transform: (page: ListNotificationsResponseDto) => ListNotificationsResponseDto,
): NotificationListCache | undefined {
  if (isNotificationsListResponse(current)) return transform(current);
  if (isNotificationsInfiniteListResponse(current)) {
    return { ...current, pages: current.pages.map(transform) };
  }
  return current;
}

/** Marks matching UNREAD rows as read in one page. Read and archived rows keep their state. */
export function markNotificationsReadInPage(
  page: ListNotificationsResponseDto,
  shouldMark: (notification: NotificationDto) => boolean,
  readAt: string,
): ListNotificationsResponseDto {
  return {
    ...page,
    items: page.items.map((notification) =>
      notification.status === NotificationStatus.Unread && shouldMark(notification)
        ? { ...notification, status: NotificationStatus.Read, readAt }
        : notification,
    ),
  };
}

/** Removes one row from a page and keeps `total` in step. A page without the row is returned as-is. */
export function removeNotificationFromPage(
  page: ListNotificationsResponseDto,
  notificationId: string,
): ListNotificationsResponseDto {
  const items = page.items.filter((notification) => notification.id !== notificationId);
  if (items.length === page.items.length) return page;

  return {
    ...page,
    items,
    total: Math.max(0, page.total - 1),
  };
}

/** Swaps in the server's copy of one row (e.g. after mark-read succeeds). Other rows are untouched. */
export function replaceNotificationInPage(
  page: ListNotificationsResponseDto,
  notification: NotificationDto,
): ListNotificationsResponseDto {
  return {
    ...page,
    items: page.items.map((current) => (current.id === notification.id ? notification : current)),
  };
}
